import api from '../lib/api';
import type { PagedResponse, PageQuery } from '../types/paging';

export interface WarehouseItem {
  id: string;
  name: string;
  code: string | null;
  address?: string | null;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
}

export interface WarehouseStockItemEx {
  productId: string;
  productName: string;
  productSku: string | null;
  quantity: number;
  locationId: string | null;
  locationCode: string | null;
  locationDescription: string | null;
}

export interface WarehouseStocksPagedResponse extends PagedResponse<WarehouseStockItemEx> {
  totalQuantity: number;
}

const base = (tenantId: string) => `/api/v1/tenants/${tenantId}/warehouses`;

const pagedParams = (opts?: PageQuery) => {
  const params: Record<string, string | number> = {};
  if (opts?.q) params.q = opts.q;
  if (opts?.page && opts.page > 0) params.page = opts.page;
  if (opts?.pageSize && opts.pageSize > 0) params.pageSize = opts.pageSize;
  return params;
};

const normalizePaged = <T>(data: PagedResponse<T> | T[] | null | undefined, page: number, pageSize: number): PagedResponse<T> => {
  if (Array.isArray(data)) return { total: data.length, page, pageSize, items: data };
  return {
    total: data?.total ?? 0,
    page: data?.page ?? page,
    pageSize: data?.pageSize ?? pageSize,
    items: data?.items ?? [],
  };
};

/** Tenant depo API servisi */
export const warehouseApi = {
  /** Depoları sayfalı döner */
  getWarehousesPaged: (tenantId: string, opts?: PageQuery, signal?: AbortSignal) =>
    api
      .get<PagedResponse<WarehouseItem> | WarehouseItem[]>(base(tenantId), { params: pagedParams(opts), signal })
      .then((r) => normalizePaged<WarehouseItem>(r.data, opts?.page ?? 1, opts?.pageSize ?? 50)),

  /** Dropdown için — tüm depolar (max 500) */
  getAllWarehouses: (tenantId: string, signal?: AbortSignal) =>
    api
      .get<PagedResponse<WarehouseItem> | WarehouseItem[]>(base(tenantId), { params: { pageSize: 500 }, signal })
      .then((r) => Array.isArray(r.data) ? r.data : (r.data?.items ?? [])),

  /** Depoya bağlı ürünlerin stok durumunu sayfalı döner */
  getWarehouseStocksPaged: (
    tenantId: string,
    warehouseId: string,
    opts?: PageQuery,
    signal?: AbortSignal,
  ) => {
    const params: Record<string, string | number> = {};
    if (opts?.q) params.q = opts.q;
    if (opts?.page && opts.page > 0) params.page = opts.page;
    if (opts?.pageSize && opts.pageSize > 0) params.pageSize = opts.pageSize;
    return api
      .get<WarehouseStocksPagedResponse>(
        `${base(tenantId)}/${warehouseId}/stocks`,
        { params, signal },
      )
      .then((r) => r.data);
  },
};
